import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, HStack, VStack, IconButton, Icon } from "@chakra-ui/react"
import { DeviceLabels, useMeetingManager, LocalVideo } from "amazon-chime-sdk-component-library-react";
import { MeetingSessionConfiguration } from "amazon-chime-sdk-js";
import { useQuery } from "react-query";
import { fetchMeeting } from "../requests/meeting";
import { ImPhoneHangUp } from "react-icons/im";
import { IoIosReverseCamera } from "react-icons/io";
import { HiVideoCamera, HiVideoCameraSlash } from "react-icons/hi2";
import { FaMicrophoneAlt, FaMicrophoneAltSlash } from "react-icons/fa";
import { motion } from 'framer-motion'

/**
 * Renders the session page
 * 
 * @returns The session page
 */
const SessionPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isCapturingVideo, setIsCapturingVideo] = useState(true);
    const [isCapturingAudio, setIsCapturingAudio] = useState(true);
    const [videoDeviceIndex, setVideoDeviceIndex] = useState(0);
    const [iconsVisibility, setIconsVisibility] = useState(true);

    /**
     * Fetches the  meeting
     */
    const meetingFunction = useCallback(() => fetchMeeting(id), [id]);

    const meetingQuery = useQuery({
        queryKey: "meeting",
        queryFn: meetingFunction,
        enabled: !!id,
    });

    const meeting = useMemo(() => meetingQuery?.data?.data?.meeting, [meetingQuery?.data?.data?.meeting]);

    const attendee = useMemo(() => meetingQuery?.data?.data?.attendee, [meetingQuery?.data?.data?.attendee]);

    const meetingManager = useMeetingManager();

    /**
     * Mutes or unmutes the video
     */
    const toggleVideo = useCallback(() => {
        if (isCapturingVideo && meetingManager?.audioVideo) {
            meetingManager.audioVideo.stopLocalVideoTile();
            setIsCapturingVideo(false);
        } else if (meetingManager?.audioVideo) {
            meetingManager.audioVideo.startLocalVideoTile();
            setIsCapturingVideo(true)
        }
    }, [meetingManager, isCapturingVideo]);

    /**
     * Mutes or unmutes the microphone
     */
    const toggleAudio = useCallback(async () => {
        if (isCapturingAudio && meetingManager?.audioVideo) {
            await meetingManager.audioVideo.stopAudioInput();
            setIsCapturingAudio(false);
        } else if (meetingManager?.audioVideo) {
            await meetingManager.audioVideo.startAudioInput(meetingManager.selectedAudioInputDevice);
            setIsCapturingAudio(true)
        }
    }, [meetingManager, isCapturingAudio]);

    /**
     * Switch between the device front and rear cameras
     * 
     * @param {boolean} Whether the function is called for the first time or not
     */
    const toggleCamera = useCallback((init = false) => {
        if (meetingManager?.audioVideo) {
            const videoDevices = meetingManager.videoInputDevices;
            const frontCameraIndex = videoDevices.findIndex(videoDevice => videoDevice.label.toLowerCase().includes('front') || videoDevice.label.toLowerCase().includes('avant'));
            const rearCameraIndex = videoDevices.findIndex(videoDevice => videoDevice.label.toLowerCase().includes('back') || videoDevice.label.toLowerCase().includes('arrière'));
            let hasChanged = false;

            if (frontCameraIndex === -1 || rearCameraIndex === -1) {
                if (videoDevices.length > 1 && videoDeviceIndex === 0) {
                    meetingManager.selectVideoInputDevice(videoDevices[1]?.deviceId);
                    setVideoDeviceIndex(1);
                    hasChanged = true;
                } else if (videoDevices.length > 0 && videoDeviceIndex === 1) {
                    meetingManager.selectVideoInputDevice(videoDevices?.[0]?.deviceId);
                    setVideoDeviceIndex(0);
                    hasChanged = true;
                }
            } else {
                const isFrontCameraSelected = videoDevices?.[videoDeviceIndex]?.label?.toLocaleLowerCase().includes('front') || videoDevices?.[videoDeviceIndex]?.label?.toLocaleLowerCase().includes('avant') || false;

                if (init || isFrontCameraSelected) {
                    meetingManager.selectVideoInputDevice(videoDevices?.[rearCameraIndex]?.deviceId);
                    setVideoDeviceIndex(rearCameraIndex);
                    hasChanged = true;
                } else {
                    meetingManager.selectVideoInputDevice(videoDevices?.[frontCameraIndex]?.deviceId);
                    setVideoDeviceIndex(frontCameraIndex);
                    hasChanged = true;
                }
            }

            if (meetingManager?.audioVideo && hasChanged) {
                meetingManager.audioVideo.startVideoInput(meetingManager.selectedVideoInputDevice);
            }
        }
    }, [meetingManager, videoDeviceIndex]);

    /**
     * Ends the call
     */
    const endCall = useCallback(async () => {
        await meetingManager.leave();
        navigate("/");
    }, [meetingManager, navigate]);

    /**
     * Toggles the icons visibility
     */
    const toggleIcons = useCallback(() => {
        setIconsVisibility((prev) => !prev);
    }, []);

    /**
     * Joins the meeting
     */
    const joinMeeting = useCallback(async () => {
        if (meeting && attendee) {
            const meetingSessionConfiguration = new MeetingSessionConfiguration(
                meeting,
                attendee
            );

            await meetingManager.join(meetingSessionConfiguration, {
                deviceLabels: DeviceLabels.AudioAndVideo,
            });

            meetingManager.audioVideo.startLocalVideoTile();

            await meetingManager.audioVideo.startVideoInput(meetingManager.selectedVideoInputDevice);
            await meetingManager.audioVideo.startAudioInput(meetingManager.selectedAudioInputDevice);

            toggleCamera(true);

            await meetingManager.start();
        }
    }, [meeting, attendee, meetingManager]);

    useEffect(() => {
        joinMeeting();
    }, [
        joinMeeting,
    ]);

    if (!meeting || !attendee) {
        return navigate("/");
    }

    return (
        <Box width={"100vw"} height={"100vh"} position={"relative"}>
            <Box width={"full"} height={"full"} position={"relative"} as={LocalVideo} onClick={toggleIcons} zIndex={500} />
            <Box display={"flex"} position={"absolute"} left={0} top={0} width={"full"} height={"90%"}>
                <VStack as={motion.div} width={"full"} alignSelf={"flex-end"} gap={"20px"} padding={"20px"} zIndex={1000} opacity={iconsVisibility ? 1 : 0} visibility={iconsVisibility ? "visible" : "hidden"} transition={"0.2s linear"}>
                    <HStack width={"full"} justifyContent={"center"} gap={"20px"}>
                        <IconButton borderRadius={"full"} icon={<Icon as={isCapturingAudio ? FaMicrophoneAlt : FaMicrophoneAltSlash} boxSize={"25px"} />} onClick={() => toggleAudio()} />
                        <IconButton borderRadius={"full"} icon={<Icon as={isCapturingVideo ? HiVideoCamera : HiVideoCameraSlash} boxSize={"25px"} />} onClick={() => toggleVideo()} />
                        <IconButton borderRadius={"full"} icon={<Icon as={IoIosReverseCamera} boxSize={"30px"} />} onClick={() => toggleCamera()} />
                    </HStack>
                    <HStack>
                        <IconButton size={"lg"} colorScheme={"red"} borderRadius={"full"} icon={<Icon as={ImPhoneHangUp} boxSize={"30px"} />} onClick={() => endCall()} />
                    </HStack>
                </VStack>
            </Box>
        </Box>
    );
}

export default React.memo(SessionPage);
